/**
 * @file FixedPositionDropdownShell atom — the single owner of the
 * fixed-position/portal/z-index dropdown pattern (SE-5528).
 * @description Compound primitive (Root/Trigger/Backdrop/Panel/Item/ItemText)
 * for trigger-anchored floating dropdowns. Pairs with the stateful
 * {@link module:components/atoms/FixedPositionDropdownShell/useDropdownPositioning}
 * hook (sibling file — atoms' index.tsx is lint-enforced stateless), which
 * supplies the `panelGeometry` and `shouldShow` values these parts consume.
 *
 * Why `position: fixed` + a document.body portal on web (SE-4953):
 * absolutely-positioned panels rendered in place paint BEHIND sibling cards —
 * GlueStack `Box` / `HStack` / `VStack` apply `z-0 relative` in their base
 * styles, so each ancestor creates an independent CSS stacking context that
 * caps the effective z-index of all descendants. A panel's `zIndex: 50` is
 * then scoped inside the innermost `z-0` context and cannot paint above
 * content in a parent context. The shell escapes this entirely: the panel is
 * `position: "fixed"` at viewport coordinates measured from the trigger
 * (`measureInWindow`) and portalled into `document.body`, so it renders in
 * the root stacking context. On native there are no CSS stacking contexts;
 * the panel stays `position: "absolute"` relative to the Root wrapper.
 *
 * Z-index contract: Backdrop 49 / Panel 50 (preserved from every migrated
 * copy). Theme: chrome uses semantic tokens only (`bg-surface-*`,
 * `border-outline-*`, `text-content-*`) — CSS-variable-backed tokens resolve
 * inside document.body portals where NativeWind `dark:` class variants do
 * not, so the shell carries no hex dictionaries.
 * @module components/atoms/FixedPositionDropdownShell
 */
import React from "react";
import { createPortal } from "react-dom";
// RN primitives are sanctioned inside the atom layer; NativeWind's global
// types add `className` to them, which keeps the dynamic-geometry `style`
// arrays correctly typed on both platforms (the vendored ui/box renders a
// raw div on web whose props drop testID/RN styles).
import {
  Modal as RNModal,
  Platform,
  Pressable as RNPressable,
  StyleSheet,
  View as RNView,
  type AccessibilityState,
  type ViewStyle,
} from "react-native";

import { deriveAriaFromAccessibilityState } from "../accessibilityState";
import { Text } from "../Text";

import type { DropdownPanelGeometry } from "./useDropdownPositioning";

export {
  default as useDropdownPositioning,
  shouldShowDropdownTree,
  type DropdownMeasurableNode,
  type DropdownPanelGeometry,
  type DropdownTriggerRect,
  type UseDropdownPositioningParams,
  type UseDropdownPositioningReturn,
} from "./useDropdownPositioning";

/** Backdrop paints below the panel but above all app content. */
export const DROPDOWN_BACKDROP_Z_INDEX = 49;
/** Panel z-index — the existing contract shared by every migrated dropdown. */
export const DROPDOWN_PANEL_Z_INDEX = 50;

/** Cached platform flag — position strategy + portal usage hang off it. */
const isWeb = Platform.OS === "web";

/**
 * Ref type for {@link FixedPositionDropdownShellTrigger}. Call sites wire the
 * hook's `triggerRef` here rather than importing raw view types (banned
 * outside the atom layer).
 */
export type FixedPositionDropdownShellTriggerRef = React.ComponentRef<
  typeof RNPressable
>;

/**
 * On web, portal `tree` into `document.body` so the overlay renders in the
 * root stacking context (see the SE-4953 file doc comment). On native, React
 * DOM portals are unavailable and the tree is returned unchanged, keeping the
 * absolute panel inside the Root's relative wrapper.
 * @param tree - The overlay element to (optionally) portal.
 * @returns The portalled element on web, or the original tree on native/SSR.
 */
function portalToBodyIfWeb(tree: React.ReactElement): React.ReactElement {
  if (!isWeb || typeof document === "undefined") {
    return tree;
  }
  return createPortal(tree, document.body) as unknown as React.ReactElement;
}

/* design-system: dynamic-style waiver — `position: "fixed"` is a web-only
 * value outside the RN style type catalog (it ships correctly in the Expo Web
 * export); resolved per-platform at module scope. */
const OVERLAY_POSITION_STYLE = {
  position: (isWeb ? "fixed" : "absolute") as ViewStyle["position"],
} as const;

const styles = StyleSheet.create({
  backdrop: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: DROPDOWN_BACKDROP_Z_INDEX,
  } as ViewStyle,
  panel: {
    zIndex: DROPDOWN_PANEL_Z_INDEX,
    elevation: 10,
  } as ViewStyle,
  /*
   * SE-5568: the Root must be the panel's containing block. On the RN new
   * architecture, `position` coming through the className pipeline does not
   * register the view as a containing block for absolutely-positioned
   * descendants, so the native panel anchored to whichever ancestor happened
   * to carry an explicit style position (device-verified on Android). Setting
   * it via a plain style prop restores trigger-anchored panels.
   */
  root: {
    position: "relative",
  } as ViewStyle,
});

/** Hoisted backdrop style pair (react-perf: no per-render array literals). */
const BACKDROP_STYLES = [styles.backdrop, OVERLAY_POSITION_STYLE];

/**
 * Composes the panel's static chrome with the measured runtime geometry.
 * design-system: dynamic-style waiver — genuine runtime geometry (viewport
 * coordinates measured from the trigger).
 * @param geometry - Style-ready geometry from `useDropdownPositioning`.
 * @returns RN style array for the panel.
 */
const panelStyles = (geometry: DropdownPanelGeometry) => [
  styles.panel,
  OVERLAY_POSITION_STYLE,
  geometry as ViewStyle,
];

/** Hoisted a11y expanded states (react-perf: no per-render object literals). */
const A11Y_EXPANDED: AccessibilityState = { expanded: true };
const A11Y_COLLAPSED: AccessibilityState = { expanded: false };

/** Hoisted a11y selected states (react-perf: no per-render object literals). */
const A11Y_SELECTED: AccessibilityState = { selected: true };
const A11Y_UNSELECTED: AccessibilityState = { selected: false };

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

/** Props for {@link FixedPositionDropdownShell}. */
export interface FixedPositionDropdownShellProps {
  /** Trigger + overlay parts. */
  readonly children: React.ReactNode;
  /** E2E test identifier. */
  readonly testID?: string;
  /** Closed-vocabulary layout tokens appended to the relative wrapper. */
  readonly className?: string;
}

/**
 * Relative wrapper that anchors the native absolute panel to the trigger.
 * On web the overlay escapes it via the document.body portal; the wrapper
 * still hosts the trigger itself.
 * @param props - Component props.
 * @param props.children - Trigger + overlay parts.
 * @param props.testID - E2E test identifier.
 * @param props.className - Closed-vocabulary layout tokens for the wrapper.
 */
export /**
 *
 */
const FixedPositionDropdownShell = ({
  children,
  testID,
  className,
}: FixedPositionDropdownShellProps) => (
  <RNView
    testID={testID}
    style={styles.root}
    className={`relative ${className ?? ""}`.trim()}
  >
    {children}
  </RNView>
);

FixedPositionDropdownShell.displayName = "FixedPositionDropdownShell";

// ---------------------------------------------------------------------------
// Trigger
// ---------------------------------------------------------------------------

/** Props for {@link FixedPositionDropdownShellTrigger}. */
export interface FixedPositionDropdownShellTriggerProps {
  /** Whether the panel is currently open (drives a11y expanded state). */
  readonly isOpen: boolean;
  /** Toggle handler. */
  readonly onPress: () => void;
  /** Closed-vocabulary chrome tokens for the trigger surface. */
  readonly className?: string;
  /** E2E test identifier. */
  readonly testID?: string;
  /** Accessibility label. */
  readonly accessibilityLabel?: string;
  /** Trigger content — label text, icons, chevron. */
  readonly children: React.ReactNode;
}

/**
 * Measurable trigger Pressable. Forward the hook's `triggerRef` here so the
 * shell can compute viewport coordinates for the fixed panel on web.
 * @param props - Component props.
 * @param props.isOpen - Whether the panel is currently open.
 * @param props.onPress - Toggle handler.
 * @param props.className - Closed-vocabulary chrome tokens.
 * @param props.testID - E2E test identifier.
 * @param props.accessibilityLabel - Accessibility label.
 * @param props.children - Trigger content.
 * @param ref - Forwarded ref for viewport-coordinate measurement.
 */
export const FixedPositionDropdownShellTrigger = React.forwardRef<
  FixedPositionDropdownShellTriggerRef,
  FixedPositionDropdownShellTriggerProps
>(
  (
    { isOpen, onPress, className, testID, accessibilityLabel, children },
    ref
  ) => (
    <RNPressable
      ref={ref}
      onPress={onPress}
      testID={testID}
      accessibilityRole="button"
      accessibilityState={isOpen ? A11Y_EXPANDED : A11Y_COLLAPSED}
      // SE-6297: web drops the object form, so emit the mapped prop too.
      {...deriveAriaFromAccessibilityState(
        isOpen ? A11Y_EXPANDED : A11Y_COLLAPSED
      )}
      accessibilityLabel={accessibilityLabel}
      className={className}
    >
      {children}
    </RNPressable>
  )
);

FixedPositionDropdownShellTrigger.displayName =
  "FixedPositionDropdownShellTrigger";

// ---------------------------------------------------------------------------
// Backdrop
// ---------------------------------------------------------------------------

/** Props for {@link FixedPositionDropdownShellBackdrop}. */
export interface FixedPositionDropdownShellBackdropProps {
  /** Dismiss handler. */
  readonly onPress: () => void;
  /** Closed-vocabulary tokens (e.g. a `bg-*` scrim tint). */
  readonly className?: string;
  /** E2E test identifier. */
  readonly testID?: string;
  /** Accessibility label. */
  readonly accessibilityLabel?: string;
}

/**
 * Full-viewport dismiss backdrop at z-index 49. `position: fixed` + portal on
 * web (root stacking context). On native (SE-5568) it renders nothing — the
 * Panel's transparent RN Modal carries its own full-window dismiss pressable
 * (`onDismiss`), because an in-tree absolute backdrop can neither cover the
 * screen reliably nor sit under the Modal-hosted panel.
 * @param props - Component props.
 * @param props.onPress - Dismiss handler.
 * @param props.className - Closed-vocabulary tokens (e.g. scrim tint).
 * @param props.testID - E2E test identifier.
 * @param props.accessibilityLabel - Accessibility label.
 */
export /**
 *
 */
const FixedPositionDropdownShellBackdrop = ({
  onPress,
  className,
  testID,
  accessibilityLabel,
}: FixedPositionDropdownShellBackdropProps) =>
  isWeb
    ? portalToBodyIfWeb(
        <RNPressable
          onPress={onPress}
          testID={testID}
          accessibilityLabel={accessibilityLabel}
          className={className}
          style={BACKDROP_STYLES}
        />
      )
    : null;

FixedPositionDropdownShellBackdrop.displayName =
  "FixedPositionDropdownShellBackdrop";

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

/** Props for {@link FixedPositionDropdownShellPanel}. */
export interface FixedPositionDropdownShellPanelProps {
  /**
   * Style-ready geometry from `useDropdownPositioning`. Renders nothing while
   * null (the SE-4953 pre-measurement guard).
   */
  readonly geometry: DropdownPanelGeometry | null;
  /**
   * Dismiss handler for the native Modal path (SE-5568): wired to the
   * in-window backdrop pressable and the Android hardware back button. Pass
   * the same close/toggle callback the Backdrop receives. No-op on web.
   */
  readonly onDismiss?: () => void;
  /** Closed-vocabulary tokens appended to the panel chrome. */
  readonly className?: string;
  /** E2E test identifier. */
  readonly testID?: string;
  /** Panel content — option rows, sections, footers. */
  readonly children: React.ReactNode;
}

/**
 * Floating option panel at z-index 50 with semantic-token chrome
 * (`bg-surface-raised` / `border-outline-subtle`). `position: fixed` +
 * document.body portal on web. On native (SE-5568) the panel renders inside
 * a transparent RN `Modal` — its own window — so it stacks above every
 * in-tree elevated subtree (global FAB, sticky chrome), with a full-window
 * dismiss pressable behind the absolutely-positioned panel box (window
 * coordinates from `measureInWindow`).
 * @param props - Component props.
 * @param props.geometry - Style-ready geometry (null = not yet measured).
 * @param props.onDismiss - Native Modal dismiss handler (backdrop + back).
 * @param props.className - Closed-vocabulary tokens appended to the chrome.
 * @param props.testID - E2E test identifier.
 * @param props.children - Panel content.
 */
export /**
 *
 */
const FixedPositionDropdownShellPanel = ({
  geometry,
  onDismiss,
  className,
  testID,
  children,
}: FixedPositionDropdownShellPanelProps) => {
  /** Web Escape-dismiss handler (attached only while the panel is mounted). */
  const onEscapeKeyDown = React.useCallback(
    (event: KeyboardEvent): void => {
      if (event.key === "Escape") onDismiss?.();
    },
    [onDismiss]
  );

  // Web has no Modal here (the panel is a document.body portal), so nothing
  // wires Escape the way native's `onRequestClose` does — the GlueStack Menu
  // this shell replaced closed on Escape, and losing it broke keyboard
  // dismissal (SE-5012 AC7). Web-only DOM listener is the sanctioned
  // capability fork.
  // Null until measured — the guard below returns before it is consumed.
  const panelBox =
    geometry == null ? null : (
      <RNView
        testID={testID}
        className={`rounded-md border border-outline-subtle bg-surface-raised shadow-hard-2 ${className ?? ""}`.trim()}
        /* design-system: dynamic-style waiver — genuine runtime geometry
         * (viewport coordinates measured from the trigger). */
        style={panelStyles(geometry)}
      >
        {children}
      </RNView>
    );

  React.useEffect(() => {
    if (!isWeb || onDismiss == null) return undefined;
    document.addEventListener("keydown", onEscapeKeyDown);
    return () => document.removeEventListener("keydown", onEscapeKeyDown);
  }, [onDismiss, onEscapeKeyDown]);

  if (panelBox == null) {
    return null;
  }
  if (isWeb) {
    return portalToBodyIfWeb(panelBox);
  }
  return (
    /*
     * No statusBarTranslucent: the Modal window must share the app window's
     * coordinate space, because the panel geometry comes from
     * `measureInWindow` (window coordinates). A translucent Modal window
     * starts at the physical screen top and shifts everything by the status
     * bar height (device-verified on Android, SE-5568).
     */
    <RNModal
      transparent
      visible
      animationType="none"
      onRequestClose={onDismiss}
    >
      <RNPressable
        accessibilityLabel="Close dropdown"
        onPress={onDismiss}
        style={BACKDROP_STYLES}
      />
      {panelBox}
    </RNModal>
  );
};

FixedPositionDropdownShellPanel.displayName = "FixedPositionDropdownShellPanel";

// ---------------------------------------------------------------------------
// Item
// ---------------------------------------------------------------------------

/** Props for {@link FixedPositionDropdownShellItem}. */
export interface FixedPositionDropdownShellItemProps {
  /** Whether this option is the currently selected value. */
  readonly isSelected: boolean;
  /** Selection handler. */
  readonly onPress: () => void;
  /** Hover-in handler (web hover feedback). */
  readonly onHoverIn?: () => void;
  /** Hover-out handler (web hover feedback). */
  readonly onHoverOut?: () => void;
  /** Closed-vocabulary tokens appended to the row. */
  readonly className?: string;
  /** E2E test identifier. */
  readonly testID?: string;
  /** Accessibility label. */
  readonly accessibilityLabel?: string;
  /** Accessibility state override (defaults to `{ selected: isSelected }`). */
  readonly accessibilityState?: AccessibilityState;
  /** Icon + text content. */
  readonly children: React.ReactNode;
}

/**
 * A single option row: menuitem role, selected-state surface tint, optional
 * web hover callbacks.
 * @param props - Component props.
 * @param props.isSelected - Whether this option is currently selected.
 * @param props.onPress - Selection handler.
 * @param props.onHoverIn - Hover-in handler (web).
 * @param props.onHoverOut - Hover-out handler (web).
 * @param props.className - Closed-vocabulary tokens appended to the row.
 * @param props.testID - E2E test identifier.
 * @param props.accessibilityLabel - Accessibility label.
 * @param props.accessibilityState - Accessibility state override.
 * @param props.children - Icon + text content.
 */
export /**
 *
 */
const FixedPositionDropdownShellItem = ({
  isSelected,
  onPress,
  onHoverIn,
  onHoverOut,
  className,
  testID,
  accessibilityLabel,
  accessibilityState,
  children,
}: FixedPositionDropdownShellItemProps) => (
  <RNPressable
    onPress={onPress}
    onHoverIn={onHoverIn}
    onHoverOut={onHoverOut}
    testID={testID}
    accessibilityRole="menuitem"
    accessibilityState={
      accessibilityState ?? (isSelected ? A11Y_SELECTED : A11Y_UNSELECTED)
    }
    // SE-6297: web drops the object form, so emit the mapped prop too.
    {...deriveAriaFromAccessibilityState(
      accessibilityState ?? (isSelected ? A11Y_SELECTED : A11Y_UNSELECTED)
    )}
    accessibilityLabel={accessibilityLabel}
    className={`flex-row items-center gap-2 px-3 py-2 ${
      isSelected ? "bg-surface-subtle" : ""
    } ${className ?? ""}`.trim()}
  >
    {children}
  </RNPressable>
);

FixedPositionDropdownShellItem.displayName = "FixedPositionDropdownShellItem";

// ---------------------------------------------------------------------------
// ItemText
// ---------------------------------------------------------------------------

/** Props for {@link FixedPositionDropdownShellItemText}. */
export interface FixedPositionDropdownShellItemTextProps {
  /** Whether this option is the currently selected value. */
  readonly isSelected: boolean;
  /** Closed-vocabulary tokens appended to the label. */
  readonly className?: string;
  /** Truncate after this many lines. */
  readonly numberOfLines?: number;
  /** Option label text. */
  readonly children: React.ReactNode;
}

/**
 * Option label text: accent color + medium weight when selected, secondary
 * content color otherwise.
 * @param props - Component props.
 * @param props.isSelected - Whether this option is currently selected.
 * @param props.className - Closed-vocabulary tokens appended to the label.
 * @param props.numberOfLines - Truncate after this many lines.
 * @param props.children - Option label text.
 */
export /**
 *
 */
const FixedPositionDropdownShellItemText = ({
  isSelected,
  className,
  numberOfLines,
  children,
}: FixedPositionDropdownShellItemTextProps) => (
  <Text
    variant={isSelected ? "body-strong" : "body"}
    numberOfLines={numberOfLines}
    className={`shrink ${
      isSelected ? "text-accent-primary" : "text-content-secondary"
    } ${className ?? ""}`.trim()}
  >
    {children}
  </Text>
);

FixedPositionDropdownShellItemText.displayName =
  "FixedPositionDropdownShellItemText";
